import { ToolkitProperty, isDefined } from '@aloft/toolkit-shared';
import { Typography } from '@aloft/toolkit-sui';
import {
  ColumnDef as ReactTableColumnDef,
  SortingFn,
  createColumnHelper,
} from '@tanstack/react-table';

import { PLACEHOLDER, UNSET_COLUMN_SIZE } from './constants.ts';
import { makeMultiSelectIsSelectedColumn } from './makeMultiSelectIsSelectedColumn.tsx';
import { makeSingleSelectIsSelectedColumn } from './makeSingleSelectIsSelectedColumn.tsx';
import { makeSortingProps } from './makeSortingProps.ts';
import {
  CustomTPColumn,
  TPColumnConfig,
  TPTableSelectRow,
  TPTableSortingState,
  isCustomTPColumnDef,
  isSystemAccessorTPColumnKey,
  isTPColumnKey,
} from './types.ts';
import { getDefaultColumnSize } from './utils/getDefaultColumnSize.ts';
import { getHeader } from './utils/getHeader.ts';
import { getTPSelector } from './utils/getTPSelector.ts';
import { getTPSortBy } from './utils/getTPSortBy.ts';
import { getToString } from './utils/getToString.ts';

export function makeCustomTPColumns<Data, Value>(
  columns: CustomTPColumn<Data>[],
  config: TPColumnConfig<Data, Value>,
  subjectProperty: ToolkitProperty | undefined,
  sortingState: TPTableSortingState,
  enableMultiSelect: boolean,
  onRowSelect: TPTableSelectRow<Data> | undefined,
): ReactTableColumnDef<Data, Value>[] {
  const columnHelper = createColumnHelper<Data>();
  const shouldPinFirstRow = false; // TODO: Ability to pin first row

  const columnDefs: ReactTableColumnDef<Data, Value>[] = columns.map((column) => {
    if (isCustomTPColumnDef(column)) {
      const isSortedDesc = !!sortingState[column.id]?.desc;
      const sortingProps = makeSortingProps<Data, unknown>(
        column.sortBy,
        column.selector,
        shouldPinFirstRow,
        isSortedDesc,
      );

      return columnHelper.accessor(column.selector, {
        id: column.id,
        header: () => <Typography variant="tableColumnTitle">{column.title}</Typography>,
        cell: (p) => {
          const val = p.getValue();
          return isDefined(val) ? column.toCell(val) : PLACEHOLDER;
        },
        size: UNSET_COLUMN_SIZE, // TODO: Add ability to specify column size

        // HACK: Lets us work around an issue with creating `sortingProps` with
        // custom column definitions.
        // It's probably not worth fixing because we're moving away from `ToolkitProperty` soon.
        ...(column.__dangerOverrideSortBy
          ? {
              sortingFn: column.__dangerOverrideSortBy as SortingFn<Data>,
            }
          : sortingProps),
      });
    }

    if (isTPColumnKey(column) || isSystemAccessorTPColumnKey(column)) {
      const id = column;
      const isSortedDesc = !!sortingState[id]?.desc;
      const selector = getTPSelector(column);
      const sortBy = getTPSortBy(column);

      const sortingProps = makeSortingProps<Data, unknown>(
        sortBy,
        (x) => config.toValue(selector, x),
        shouldPinFirstRow,
        isSortedDesc,
        (rowA, rowB) => {
          const rowAValue = config.toValue(selector, rowA);
          const rowBValue = config.toValue(selector, rowB);
          return config.toCompareValues(rowAValue, rowBValue);
        },
        subjectProperty,
      );

      return {
        accessorFn: (row) => config.toValue(selector, row),
        id,
        header: () => <Typography variant="tableColumnTitle">{getHeader(column)}</Typography>,
        cell: (p) => {
          const value = p.getValue();
          return config.toCell({
            value,
            toString: getToString(column, subjectProperty),
          });
        },
        size: getDefaultColumnSize(column), // TODO: Add ability to specify column size
        ...sortingProps,
      };
    }

    throw new Error(`Invalid column definition: ${JSON.stringify(column, null, 2)}`);
  });

  const makeIsSelectedColumnFn = enableMultiSelect
    ? makeMultiSelectIsSelectedColumn
    : makeSingleSelectIsSelectedColumn;

  return [makeIsSelectedColumnFn(false, onRowSelect), ...columnDefs];
}
